import React, { useEffect, useState } from 'react';
import SupplierPageLayout from '../components/SupplierPageLayout';
import {
  SupplierOrganization,
  getOrganizations,
  getSupplierProfile,
  registerSupplierProfile,
  saveSupplierProfile,
} from '../services/supplierApi';

interface ProfileForm {
  legalName: string;
  tradeName: string;
  gstin: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  categories: string;
  isMarketplaceVisible: boolean;
}

const GSTIN_RE = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

const orgLabel = (org: SupplierOrganization) => org.tradeName || org.legalName || `Organisation #${org.id}`;

const categoriesText = (raw?: string): string => {
  if (!raw) return '';
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.join(', ') : String(raw);
  } catch {
    return raw;
  }
};

const EMPTY_FORM: ProfileForm = {
  legalName: '',
  tradeName: '',
  gstin: '',
  phone: '',
  email: '',
  address: '',
  city: '',
  categories: '',
  isMarketplaceVisible: false,
};

const toForm = (org: SupplierOrganization): ProfileForm => ({
  legalName: org.legalName ?? '',
  tradeName: org.tradeName ?? '',
  gstin: org.gstin ?? '',
  phone: org.phone ?? '',
  email: org.email ?? '',
  address: org.address ?? '',
  city: org.city ?? '',
  categories: categoriesText(org.categories),
  isMarketplaceVisible: !!org.isMarketplaceVisible,
});

const Profile: React.FC = () => {
  const [orgs, setOrgs] = useState<SupplierOrganization[]>([]);
  const [orgId, setOrgId] = useState<number | null>(null);
  const [form, setForm] = useState<ProfileForm | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    getOrganizations()
      .then((data) => {
        const list: SupplierOrganization[] = data.organizations ?? [];
        setOrgs(list);
        setOrgId(list.length > 0 ? list[0].id : null);
        if (list.length === 0) {
          setForm(EMPTY_FORM);
          setLoading(false);
        }
      })
      .catch((e: Error) => {
        setError(e.message);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (orgId == null) return;
    setLoading(true);
    getSupplierProfile(orgId)
      .then((data) => setForm(toForm(data.organization)))
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [orgId]);

  const updateField =
    (field: Exclude<keyof ProfileForm, 'isMarketplaceVisible'>) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const value = e.target.value;
      setForm((prev) => (prev ? { ...prev, [field]: value } : prev));
    };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form) return;
    setError('');
    setNotice('');
    const gstin = form.gstin.trim().toUpperCase();
    if (!form.legalName.trim()) {
      setError('Legal name is required.');
      return;
    }
    if (gstin && !GSTIN_RE.test(gstin)) {
      setError('Enter a valid 15-character GSTIN, e.g. 29ABCDE1234F1Z5.');
      return;
    }
    const categories = form.categories
      .split(',')
      .map((c) => c.trim())
      .filter(Boolean);
    setBusy(true);
    try {
      const payload = {
        legalName: form.legalName.trim(),
        tradeName: form.tradeName.trim(),
        gstin,
        phone: form.phone.trim(),
        email: form.email.trim(),
        address: form.address.trim(),
        city: form.city.trim(),
        categories: categories.length ? JSON.stringify(categories) : '',
        isMarketplaceVisible: form.isMarketplaceVisible,
      };
      const data = orgId == null ? await registerSupplierProfile(payload) : await saveSupplierProfile(orgId, payload);
      const saved: SupplierOrganization = data.organization;
      setForm(toForm(saved));
      if (orgId == null) {
        setOrgs([saved]);
        setOrgId(saved.id);
        setNotice('Business registered. You can now manage your catalog and inventory.');
      } else {
        setOrgs((prev) => prev.map((o) => (o.id === saved.id ? { ...o, ...saved } : o)));
        setNotice('Profile saved.');
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <SupplierPageLayout
      title="Business Profile"
      subtitle="Update your trade name, GSTIN, and contact details for restaurants."
      icon="fa-user-o"
      breadcrumbs={[
        { label: 'Dashboard', to: '/supplier/dashboard' },
        { label: 'Profile' },
      ]}
    >
      {error && <div className="sp-alert sp-alert--error">{error}</div>}
      {notice && <div className="sp-alert sp-alert--success">{notice}</div>}

      {loading ? (
        <div className="sp-loading">Loading profile…</div>
      ) : !form ? null : (
        <form className="sp-card sp-profile" onSubmit={handleSave}>
          {orgId == null && (
            <div className="sp-card__header sp-profile__intro">
              <span className="sp-profile__intro-icon" aria-hidden="true">
                <i className="fa fa-building-o" />
              </span>
              <div>
                <h2 className="sp-profile__intro-title">Register your business</h2>
                <p className="sp-profile__intro-text">
                  Add your business details so restaurants can find you and you can manage your catalog. Only the legal
                  name is required — you can fill in the rest later.
                </p>
              </div>
            </div>
          )}
          {orgs.length > 1 && orgId != null && (
            <div className="sp-card__header">
              <select
                className="sp-input"
                style={{ maxWidth: 240 }}
                value={orgId}
                onChange={(e) => setOrgId(Number(e.target.value))}
                aria-label="Organisation"
              >
                {orgs.map((o) => (
                  <option key={o.id} value={o.id}>
                    {orgLabel(o)}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="sp-card__body">
            <section className="sp-profile__section">
              <div className="sp-profile__section-head">
                <h3>Business details</h3>
                <p>How your business appears on purchase orders and invoices.</p>
              </div>
              <div className="sp-profile__grid">
                <label className="sp-profile__field">
                  <span className="sp-profile__label">
                    Legal name <span className="sp-profile__required">*</span>
                  </span>
                  <input
                    className="sp-profile__input"
                    value={form.legalName}
                    onChange={updateField('legalName')}
                    maxLength={200}
                    placeholder="e.g. Sharma Fresh Produce Pvt Ltd"
                    required
                  />
                </label>
                <label className="sp-profile__field">
                  <span className="sp-profile__label">Trade name</span>
                  <input
                    className="sp-profile__input"
                    value={form.tradeName}
                    onChange={updateField('tradeName')}
                    maxLength={200}
                    placeholder="e.g. Sharma Fresh"
                  />
                  <span className="sp-profile__hint">The name restaurants know you by.</span>
                </label>
                <label className="sp-profile__field">
                  <span className="sp-profile__label">GSTIN</span>
                  <input
                    className="sp-profile__input sp-profile__input--upper"
                    value={form.gstin}
                    onChange={updateField('gstin')}
                    maxLength={15}
                    placeholder="e.g. 29ABCDE1234F1Z5"
                  />
                  <span className="sp-profile__hint">15 characters. Leave blank if not registered.</span>
                </label>
              </div>
            </section>

            <section className="sp-profile__section">
              <div className="sp-profile__section-head">
                <h3>Contact &amp; location</h3>
                <p>Restaurants use these to reach you about orders and deliveries.</p>
              </div>
              <div className="sp-profile__grid">
                <label className="sp-profile__field">
                  <span className="sp-profile__label">Phone</span>
                  <input
                    className="sp-profile__input"
                    type="tel"
                    value={form.phone}
                    onChange={updateField('phone')}
                    maxLength={20}
                    placeholder="e.g. 98765 43210"
                  />
                </label>
                <label className="sp-profile__field">
                  <span className="sp-profile__label">Email</span>
                  <input
                    className="sp-profile__input"
                    type="email"
                    value={form.email}
                    onChange={updateField('email')}
                    maxLength={200}
                    placeholder="e.g. orders@sharmafresh.in"
                  />
                </label>
                <label className="sp-profile__field">
                  <span className="sp-profile__label">City</span>
                  <input
                    className="sp-profile__input"
                    value={form.city}
                    onChange={updateField('city')}
                    maxLength={100}
                    placeholder="e.g. Bengaluru"
                  />
                </label>
                <label className="sp-profile__field sp-profile__field--full">
                  <span className="sp-profile__label">Address</span>
                  <textarea
                    className="sp-profile__input sp-profile__textarea"
                    value={form.address}
                    onChange={updateField('address')}
                    maxLength={500}
                    rows={3}
                    placeholder="Shop / warehouse number, street, area, pincode"
                  />
                </label>
              </div>
            </section>

            <section className="sp-profile__section">
              <div className="sp-profile__section-head">
                <h3>What you supply</h3>
                <p>Helps restaurants discover you in the marketplace.</p>
              </div>
              <div className="sp-profile__grid">
                <label className="sp-profile__field sp-profile__field--full">
                  <span className="sp-profile__label">Categories</span>
                  <input
                    className="sp-profile__input"
                    value={form.categories}
                    onChange={updateField('categories')}
                    placeholder="e.g. Vegetables, Dairy, Dry goods"
                  />
                  <span className="sp-profile__hint">Separate categories with commas (up to 20).</span>
                </label>
                <label className="sp-profile__toggle sp-profile__field--full">
                  <input
                    type="checkbox"
                    checked={form.isMarketplaceVisible}
                    onChange={(e) =>
                      setForm((prev) => (prev ? { ...prev, isMarketplaceVisible: e.target.checked } : prev))
                    }
                  />
                  <span>
                    <span className="sp-profile__toggle-title">Show my business in the restaurant marketplace</span>
                    <span className="sp-profile__toggle-text">
                      Restaurants on Peppar can find you and send purchase orders directly.
                    </span>
                  </span>
                </label>
              </div>
            </section>
          </div>

          <div className="sp-profile__footer">
            <button type="submit" className="sp-btn sp-btn--primary" disabled={busy}>
              {busy ? 'Saving…' : orgId == null ? 'Register business' : 'Save profile'}
            </button>
          </div>
        </form>
      )}
    </SupplierPageLayout>
  );
};

export default Profile;
